import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { getLoanJumpTargets, isLoanRoute, JumpTarget } from 'config/loanJumpTargets'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { getCreditDenial, getHmda, getLoanEstimate, getServicingNotes } from 'services'
import { setLoanNumber } from 'utils/setLoanNumber'

import { getApplicationMenuTargets } from './applicationMenuTargets'
import { getOriginationStaticFieldTargets, originationDynamicResToTargets } from './originationFieldTargets'
import { getServicingStaticFieldTargets, servicingInfoResToTargets } from './servicingFieldTargets'

const RESULT_LIMIT = 40

type Scored = { t: JumpTarget; score: number; idx: number }

// Backend-defined field labels, cached per loan so remounting the header does not refetch them.
const backendFieldCache = new Map<string, JumpTarget[]>()

export const LoanFieldLookup = ({ loanNumber }: { loanNumber?: string | number }) => {
  const history = useHistory()
  const location = useLocation()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  // Only show inside a loan; resolve the active loan number for deep links.
  const loanDetail = useSelector((state: any) => state.loanDetail)
  const onLoan = isLoanRoute(location.pathname)
  const activeLoanNo =
    loanNumber && String(loanNumber) !== '-' && Number(loanNumber) > 0
      ? loanNumber
      : Number(loanDetail?.loanNumber) > 0
      ? loanDetail.loanNumber
      : setLoanNumber()

  // Rebuild the catalog once the loan-field config is available in the store.
  const loanFieldsConfig = useSelector((state: any) => state.globalConfigReducer?.[GlobalConfigType.LoanFields])
  const servicingStatic = useMemo(() => getServicingStaticFieldTargets(), [])
  const originationStatic = useMemo(() => getOriginationStaticFieldTargets(), [])
  const cacheKey = String(activeLoanNo || '')
  const [backendFields, setBackendFields] = useState<JumpTarget[]>(() => backendFieldCache.get(cacheKey) || [])
  const fetchedBackendFields = useRef(backendFieldCache.has(cacheKey))

  // Switching loans invalidates the per-loan field labels.
  useEffect(() => {
    setBackendFields(backendFieldCache.get(cacheKey) || [])
    fetchedBackendFields.current = backendFieldCache.has(cacheKey)
  }, [cacheKey])

  const targets = useMemo(
    () => [
      ...getLoanJumpTargets(),
      ...getApplicationMenuTargets(),
      ...originationStatic,
      ...servicingStatic,
      ...backendFields,
    ],
    [loanFieldsConfig, loanDetail, originationStatic, servicingStatic, backendFields],
  )

  // A few screens have their field definitions built by the backend per loan rather than in a
  // constants file (Servicing Information, HMDA, Loan Estimate, Credit Denial). Pull those in
  // once, on first use, so they are searchable alongside the statically-defined screens.
  const loadBackendFields = () => {
    if (fetchedBackendFields.current) return
    fetchedBackendFields.current = true

    const append = (targetsToAdd: JumpTarget[]) => {
      if (!targetsToAdd.length) return
      backendFieldCache.set(cacheKey, [...(backendFieldCache.get(cacheKey) || []), ...targetsToAdd])
      setBackendFields((prev) => [...prev, ...targetsToAdd])
    }

    getServicingNotes()
      .then((res: any) => append(servicingInfoResToTargets(res)))
      .catch(() => {})
    getHmda()
      .then((res: any) => append(originationDynamicResToTargets('hmda', res)))
      .catch(() => {})
    getLoanEstimate()
      .then((res: any) => append(originationDynamicResToTargets('loanEstimate', res)))
      .catch(() => {})
    getCreditDenial()
      .then((res: any) => append(originationDynamicResToTargets('creditDenial', res)))
      .catch(() => {})
  }

  const results = useMemo<JumpTarget[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      // Empty query, focused: surface the primary screens as a starting point.
      return targets.filter((t) => t.group === 'Overview' || t.group === 'Servicing' || t.group === 'Underwriting').slice(0, RESULT_LIMIT)
    }
    const scored: Scored[] = []
    for (const t of targets) {
      const label = t.label.toLowerCase()
      const hay = `${label} ${t.group.toLowerCase()} ${(t.keywords || '').toLowerCase()}`
      if (hay.indexOf(q) === -1) continue
      let score = 3
      if (label.startsWith(q)) score = 0
      else if (label.includes(q)) score = 1
      else score = 2
      scored.push({ t, score, idx: label.indexOf(q) === -1 ? 999 : label.indexOf(q) })
    }
    scored.sort((a, b) => a.score - b.score || a.idx - b.idx || a.t.label.localeCompare(b.t.label))
    return scored.slice(0, RESULT_LIMIT).map((s) => s.t)
  }, [query, targets])

  useEffect(() => setActiveIndex(0), [query, open])

  // Global shortcut: Cmd/Ctrl-K focuses the lookup.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen(true)
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Close when clicking outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Keep the highlighted row in view.
  useLayoutEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, results])

  const go = (t: JumpTarget) => {
    setOpen(false)
    setQuery('')
    history.push(t.to(activeLoanNo))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (results[activeIndex]) go(results[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  const showResults = open && results.length > 0

  // Only render inside a loan.
  if (!onLoan || !activeLoanNo) return null

  return (
    <div ref={containerRef} className="relative w-56 lg:w-72">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder="Jump to screen or field…"
          className="block w-full rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-sky-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            // Only fetch the backend-defined field sets once the user is actually searching
            // for a field; an empty box just lists the primary screens.
            if (e.target.value.trim()) loadBackendFields()
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            onMouseDown={(e) => {
              e.preventDefault()
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label="Clear"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showResults && (
        <div
          ref={listRef}
          className="absolute right-0 z-[60] mt-2 max-h-[60vh] w-[min(28rem,calc(100vw-2rem))] overflow-auto rounded-xl border border-slate-100 bg-white py-1 shadow-2xl"
        >
          {results.map((t, idx) => (
            <div
              key={t.id}
              data-idx={idx}
              className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm ${
                idx === activeIndex ? 'bg-shade-blue/10' : 'hover:bg-gray-50'
              }`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault()
                go(t)
              }}
            >
              <span className="truncate font-medium text-gray-800">{t.label}</span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-gray-500">{t.group}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
